import { Editor } from '@tiptap/react'
import { createPortal } from 'react-dom'
import { Undo2, Redo2, Bold, List, IndentIncrease, IndentDecrease, Link2, Heading1, Heading2, Heading3, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useKeyboardHeight } from '@/hooks/useKeyboardHeight'
import { useIsMobile } from '@/hooks/useMediaQuery'

type ToolbarItem =
  | { type: 'separator' }
  | {
      icon: LucideIcon
      label: string
      action: () => void
      isActive?: () => boolean
      canExecute?: () => boolean
    }

interface EditorToolbarProps {
  editor: Editor | null
  disabled?: boolean
  /** Height of action bar below toolbar on mobile (px) */
  actionBarHeight?: number
  /** Height of bottom navigation on mobile (px), default 56 (h-14) */
  mobileNavHeight?: number
  /** Callback to insert a link */
  onLinkClick?: () => void
  /** Hide the border (when embedded in a container that has its own border) */
  noBorder?: boolean
  /** On mobile the toolbar is fixed + portaled, so it would otherwise bleed
   *  onto other tabs while its (hidden) editor stays mounted. Pass false when
   *  the editor's pane isn't the active one to suppress it. Default true. */
  active?: boolean
}

// Get safe area inset bottom (for notched devices)
function getSafeAreaBottom(): number {
  if (typeof window === 'undefined') return 0
  const root = document.documentElement
  const safeAreaStr = getComputedStyle(root).getPropertyValue('--safe-area-inset-bottom') ||
                      getComputedStyle(root).getPropertyValue('env(safe-area-inset-bottom)')
  if (!safeAreaStr) {
    // Fallback: check if CSS env() is supported by looking at computed style
    const temp = document.createElement('div')
    temp.style.paddingBottom = 'env(safe-area-inset-bottom)'
    document.body.appendChild(temp)
    const computed = parseFloat(getComputedStyle(temp).paddingBottom) || 0
    document.body.removeChild(temp)
    return computed
  }
  return parseFloat(safeAreaStr) || 0
}

export function EditorToolbar({ editor, disabled = false, actionBarHeight = 0, mobileNavHeight = 56, onLinkClick, noBorder = false, active = true }: EditorToolbarProps) {
  const keyboardHeight = useKeyboardHeight()
  const isMobile = useIsMobile()

  if (!editor) return null

  // On mobile with keyboard open: position at keyboard height (just above keyboard)
  // On mobile without keyboard: position above nav bar (56px) + safe area + any action bar
  // On desktop: md:relative overrides inline style
  const safeAreaBottom = isMobile ? getSafeAreaBottom() : 0
  const mobileBottom = keyboardHeight > 0 ? keyboardHeight : (mobileNavHeight + safeAreaBottom + actionBarHeight)

  // Heading buttons only when the editor's schema actually has a heading node —
  // this toolbar is shared by several editors; one that omits headings simply
  // won't show them (no behavior change there). toggleHeading both ADDS and
  // REMOVES: clicking H1 on a non-heading makes it H1; clicking H1 on an
  // existing H1 turns it back into a paragraph.
  const hasHeading = !!editor.schema.nodes.heading
  const headingButtons: ToolbarItem[] = hasHeading
    ? [
        {
          icon: Heading1,
          label: 'Heading 1',
          action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          isActive: () => editor.isActive('heading', { level: 1 }),
        },
        {
          icon: Heading2,
          label: 'Heading 2',
          action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          isActive: () => editor.isActive('heading', { level: 2 }),
        },
        {
          icon: Heading3,
          label: 'Heading 3',
          action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          isActive: () => editor.isActive('heading', { level: 3 }),
        },
      ]
    : []

  const buttons: ToolbarItem[] = [
    {
      icon: Undo2,
      label: 'Undo',
      action: () => editor.chain().focus().undo().run(),
      canExecute: () => editor.can().undo(),
    },
    {
      icon: Redo2,
      label: 'Redo',
      action: () => editor.chain().focus().redo().run(),
      canExecute: () => editor.can().redo(),
    },
    { type: 'separator' as const },
    ...headingButtons,
    {
      icon: Bold,
      label: 'Bold',
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
    },
    {
      icon: List,
      label: 'Bullet List',
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList'),
    },
    {
      icon: Link2,
      label: 'Link (⌘K)',
      action: () => onLinkClick?.(),
      isActive: () => editor.isActive('link'),
    },
    { type: 'separator' as const },
    {
      icon: IndentDecrease,
      label: 'Outdent',
      action: () => editor.chain().focus().liftListItem('listItem').run(),
      canExecute: () => editor.can().liftListItem('listItem'),
    },
    {
      icon: IndentIncrease,
      label: 'Indent',
      action: () => editor.chain().focus().sinkListItem('listItem').run(),
      canExecute: () => editor.can().sinkListItem('listItem'),
    },
  ]

  const toolbarContent = (
    <div
      style={{ bottom: isMobile ? mobileBottom : undefined }}
      className={cn(
        'flex-shrink-0 bg-background',
        'py-2',
        'flex items-center justify-center gap-1',
        // Mobile: fixed positioning (bottom set via style), with top border
        // Desktop: relative positioning
        isMobile
          ? 'fixed left-0 right-0 z-50 border-t border-border'
          : noBorder ? 'relative' : 'relative border-b border-border'
      )}
    >
      {buttons.map((button, index) => {
        if (!('icon' in button)) {
          return (
            <div
              key={`sep-${index}`}
              className="w-px self-stretch bg-border mx-2"
            />
          )
        }

        const Icon = button.icon
        const isDisabled = disabled || (button.canExecute && !button.canExecute())
        const isActive = button.isActive?.()

        return (
          <button
            key={button.label}
            onClick={button.action}
            disabled={isDisabled}
            title={button.label}
            className={cn(
              'p-2 rounded transition-colors',
              isActive
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
              isDisabled && 'opacity-30 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground'
            )}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} />
          </button>
        )
      })}
    </div>
  )

  // On mobile, render via portal to escape any overflow:hidden containers.
  if (isMobile) {
    // Suppress when this editor's pane isn't the one on screen — otherwise the
    // fixed/portaled bar bleeds onto the other tabs (e.g. covers the Concierge
    // chat input) while the editor stays mounted-but-hidden.
    if (!active) return null
    // Portal into the editor's themed root (.concierge-root) rather than
    // <body>: the dark token vars (--color-background etc.) are scoped to that
    // root, so portaling to <body> resolved bg-background to the light default
    // (the toolbar showed white). Falls back to <body> if not found.
    const themedRoot = editor.view.dom.closest('.concierge-root') as HTMLElement | null
    return createPortal(toolbarContent, themedRoot ?? document.body)
  }

  return toolbarContent
}
